import omit from "lodash/omit";

export const createDummyARIEntry = (dateString, room, date, currencyCode) => ({
  roomtypeId: room.id,
  date: dateString,
  day: date.toLocaleDateString("en-US", { weekday: "long" }),
  adultPrices: [],
  priceData: {},
  sourceTax: "0.00",
  isOpen: true,
  inventory: 0,
  minLos: 0,
  maxLos: 28,
  minLeadTime: 0,
  currency: currencyCode,
});

// Iterates in UTC only: stepping with setDate() advances local wall-clock
// time, which drifts an hour across DST transitions and then drops the last
// day (fall-back) or duplicates one (spring-forward) once the consumer
// formats with toISOString(). UTC days are always exactly 24h.
export const generateDateRange = (startDate, endDate) => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let cursor = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endUtc = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  const dates = [];
  while (cursor <= endUtc) {
    dates.push(new Date(cursor));
    cursor += DAY_MS;
  }
  return dates;
};

export const getAdultPrices = (priceData) => {
  let adultPrices = [];
  if (priceData?.occupancyPriceMap) {
    const priceMapKeys = Object.keys(priceData.occupancyPriceMap).filter(
      (key) => key.includes(":0")
    );
    priceMapKeys.forEach((key) => {
      adultPrices.push(priceData?.occupancyPriceMap[key]?.price);
    });
  } else {
    adultPrices = priceData.adult;
  }
  return adultPrices;
};

export const getDateARIDataPrice = (
  adultPrices,
  maxAdultOccupancy,
  singleUse
) => {
  let price = null;
  // Check if singleUse is available and maxAdultOccupancy is 1 for Dingus and CloudBed
  if (maxAdultOccupancy === 1 && singleUse) {
    price = singleUse;
  } else if (adultPrices?.length > 0) {
    if (maxAdultOccupancy === 1) {
      price = singleUse || adultPrices[0];
    } else if (adultPrices?.length > 1) {
      price = adultPrices[1];
    }
  }

  // Formatting and rounding all the price values to 2 decimal places
  return price != null && price !== "" && price !== undefined
    ? (typeof price === "number"
        ? price
        : parseFloat(String(price).replace(/,/g, ""))
      ).toFixed(2)
    : null;
};

export const processedDateARIData = (ariData, maxAdultOccupancy) => {
  return {
    ...omit(ariData, ["priceData"]),
    maxAdultOccupancy,
    singleUse: ariData?.priceData?.singleUse,
    price: getDateARIDataPrice(
      ariData.adultPrices,
      maxAdultOccupancy,
      ariData?.priceData?.singleUse
    ),
  };
};

export const transformAriResponse = (aris) => {
  return (aris || []).map((ari) => {
    /**
     * Handling for occupancyPriceMap which is an object with occupancy keys for CRS dEdge
     */
    const _ariData = ari?.price?.[0];
    return _ariData?.occupancyPriceMap
      ? {
          ...omit(ari, ["price", "occupancyPriceMap"]),
          priceData: ari.price?.[0] || {},
          isOpen:
            ari?.isOpen && _ariData?.occupancyPriceMap?.["1:0"]?.status === 1,
          adultPrices: getAdultPrices(_ariData),
          sourceTax: Number(ari.sourceTax || 0).toFixed(2),
        }
      : {
          ...omit(ari, ["price"]),
          priceData: ari.price?.[0] || {},
          adultPrices: _ariData?.adult,
          sourceTax: Number(ari.sourceTax || 0).toFixed(2),
        };
  });
};

export const getPrioritizedRoomBatches = (
  rooms,
  priorityRoomtypeId,
  batchSize,
  threshold
) => {
  if (!rooms?.length) {
    return [];
  }
  if (rooms.length <= threshold) {
    return [rooms];
  }

  const priorityRoom = rooms.find(({ id }) => id === priorityRoomtypeId);
  const orderedRooms = priorityRoom
    ? [priorityRoom, ...rooms.filter(({ id }) => id !== priorityRoomtypeId)]
    : [...rooms];

  const batches = [];
  for (let i = 0; i < orderedRooms.length; i += batchSize) {
    batches.push(orderedRooms.slice(i, i + batchSize));
  }
  return batches;
};

export const mergeAriDataByRoomtype = (
  existingData,
  newRecords,
  fetchedRoomtypeIds
) => {
  const fetchedIds = new Set(fetchedRoomtypeIds);
  return [
    ...existingData.filter(({ roomtypeId }) => !fetchedIds.has(roomtypeId)),
    ...newRecords,
  ];
};

export const getSanitizeDataForMissingAris = (
  data,
  startDate,
  endDate,
  rooms
) => {
  const dateRange = generateDateRange(startDate, endDate);
  const updatedData = [...data];
  dateRange.forEach((date) => {
    const dateString = date.toISOString().split("T")[0];
    rooms.forEach((room) => {
      const dateExists = data.some(
        (item) => item.date === dateString && item.roomtypeId === room.id
      );
      if (!dateExists) {
        updatedData.push(
          createDummyARIEntry(dateString, room, date, data[0]?.currency)
        );
      }
    });
  });

  return updatedData;
};
